import React, { useMemo } from 'react';
import { TreeView } from '@material-ui/lab';
import { TreeNode, getNodePosition, ITreeNodeCallbacks } from './TreeNode';
import { SkeletonTree } from 'loaders/SkeletonTree';
import classNames from 'classnames';
import './Tree.less';
import { ITreeNode, ITreeNodeContentProps } from '../CommonTree/CommonTreeNode';
import { findNodesList } from './useTreeStore';

export interface ITreeProps<T> extends ITreeNodeCallbacks<T> {
  id?: string;
  classes?: {
    root?: string;
    node?: string;
  };
  nodeContentComponent?: (props: React.PropsWithChildren<ITreeNodeContentProps<T>>) => JSX.Element;
  emptyComponent?: () => JSX.Element;
  loading?: {
    isLoading: boolean;
    height: number;
  };
  sortNodes?: (a: ITreeNode<T>, b: ITreeNode<T>) => number;
  scroll?: ITreeScroll;
  nodes: ITreeNode<T>[];
  searchString?: string;
}

export interface ITreeScroll {
  scrollType: ScrollType;
  customCallback?: (element: HTMLElement) => void;
}

export enum ScrollType {
  none,
  auto,
  custom,
}

/**
 * Tree Component props:
 *
 * Tree model props:
 */

function getTreeClassNames(additionalClassName?: string) {
  return classNames('dpc-tree', additionalClassName);
}

export function Tree<T>({
  id,
  loading,
  onNodeKeyDown,
  onNodeExpand,
  onNodeFold,
  onNodeSelect,
  onNodeMouseDown,
  onNodeMouseUp,
  onNodeKeyUp,
  onNodeMouseOver,
  onNodeMouseOut,
  nodeContentComponent,
  onNodeContextMenu,
  sortNodes,
  emptyComponent,
  classes,
  nodes,
  scroll,
  searchString,
  ...props
}: ITreeProps<T>) {
  const expandedNodes: string[] = useMemo(() => {
    return findNodesList(node => !!node.isExpanded, nodes).map(n => n.nodeId);
  }, [nodes]);

  if (loading?.isLoading) {
    return <SkeletonTree height={loading.height} />;
  }

  if (emptyComponent != null && (nodes == null || nodes.length === 0)) {
    return emptyComponent();
  }

  return (
    <TreeView expanded={expandedNodes} id={id} className={getTreeClassNames(classes?.root)} {...props}>
      {(sortNodes ? nodes?.sort(sortNodes) : nodes).map((node: ITreeNode<T>, i, arr) => {
        return (
          <TreeNode
            key={node.nodeId}
            isRoot={true}
            nodePosition={getNodePosition(i, arr.length)}
            onNodeKeyDown={onNodeKeyDown}
            contentComponent={nodeContentComponent}
            onNodeExpand={onNodeExpand}
            onNodeFold={onNodeFold}
            onNodeSelect={onNodeSelect}
            onNodeMouseUp={onNodeMouseUp}
            onNodeMouseDown={onNodeMouseDown}
            onNodeKeyUp={onNodeKeyUp}
            onNodeMouseOver={onNodeMouseOver}
            onNodeMouseOut={onNodeMouseOut}
            onNodeContextMenu={onNodeContextMenu}
            sortNodes={sortNodes}
            searchString={searchString}
            scroll={scroll}
            classes={{
              content: classes?.node,
            }}
            {...node}
          />
        );
      })}
    </TreeView>
  );
}
